import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import type * as API from '@/types/api';
import { useAccountStore, useSubscriptionStore } from '@nimbitlabs/features';

interface ServicesState {
  services: API.ServiceDto[];
  loading: boolean;
  error: string | null;
  
  fetchServices: () => Promise<void>;
  addService: (serviceData: API.CreateServiceDto) => Promise<API.ServiceDto>;
  updateService: (id: string, serviceData: API.UpdateServiceDto) => Promise<void>;
  deleteService: (serviceId: string) => Promise<void>;
  getServiceById: (id: string) => Promise<API.ServiceDto>;
  hasReachedLimit: () => boolean;
  getServiceLimit: () => { current: number; limit: number | null; hasReachedLimit: boolean };
  clearError: () => void;
}

const servicesStore = create<ServicesState>()(
  subscribeWithSelector((set, get) => ({
  services: [],
  loading: false,
  error: null,
  
  fetchServices: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.services.getAll();
      set({ services: data, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch services', loading: false });
    }
  },
  
  addService: async (serviceData: API.CreateServiceDto) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticService: API.ServiceDto = {
      id: tempId,
      ...serviceData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      consumers: serviceData.consumers || [],
      isLocked: false,
    };

    set(state => ({ services: [optimisticService, ...state.services] }));

    try {
      const created = await api.services.create(serviceData);
      set(state => ({
        services: state.services.map(s => s.id === tempId ? created : s)
      }));
      return created;
    } catch (error) {
      set(state => ({
        services: state.services.filter(s => s.id !== tempId),
        error: error instanceof Error ? error.message : 'Failed to create service'
      }));
      throw error;
    }
  },
  
  updateService: async (id: string, serviceData: API.UpdateServiceDto) => {
    const original = get().services.find(s => s.id === id);
    
    set(state => ({
      services: state.services.map(s => 
        s.id === id ? { ...s, ...serviceData, updatedAt: new Date().toISOString() } : s
      )
    }));
    
    try {
      const updated = await api.services.update(id, serviceData);
      set(state => ({
        services: state.services.map(s => s.id === id ? updated : s)
      }));
    } catch (error) {
      if (original) {
        set(state => ({
          services: state.services.map(s => s.id === id ? original : s)
        }));
      }
      throw error;
    }
  },
  
  deleteService: async (serviceId: string) => {
    const original = get().services;
    
    set(state => ({
      services: state.services.filter(s => s.id !== serviceId)
    }));
    
    try {
      await api.services.delete(serviceId);
    } catch (error) {
      set({ services: original });
      throw error;
    }
  },
  
  getServiceById: async (id: string) => {
    const cached = get().services.find(s => s.id === id);
    if (cached) return cached;
    
    const data = await api.services.getById(id);
    set(state => ({
      services: [...state.services.filter(s => s.id !== id), data]
    }));
    return data;
  },
  
  hasReachedLimit: () => {
    const { limits } = useSubscriptionStore.getState();
    if (!limits) return true; // No data = assume limit reached
    const serviceLimit = limits.features?.services?.limit;
    if (serviceLimit === undefined) return false; // No limit = never reached
    // Use actual count from store, not backend usage
    const currentCount = get().services.length;
    return currentCount >= serviceLimit;
  },
  
  getServiceLimit: () => {
    const { limits } = useSubscriptionStore.getState();
    // Use actual count from store, not backend usage
    const current = get().services.length;
    const limit = limits?.features?.services?.limit ?? null;
    const hasReachedLimit = limit !== null && current >= limit;
    return { current, limit, hasReachedLimit };
  },
  
  clearError: () => set({ error: null }),
  }))
);

export const useServicesStore = servicesStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      servicesStore.getState().fetchServices();
    } else {
      servicesStore.setState({ services: [] });
    }
  }
);
